import { Controller, type Control, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { Button, Calendar, Label, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { cn } from '@/lib/utils';

type TProps<TFieldValues extends FieldValues> = {
	name: Path<TFieldValues>;
	label: string;
	placeholder: string;
	control: Control<TFieldValues>;
	// days before this one cannot be picked
	minDate?: Date;
	// the time a first picked day gets, e.g. 23:59 for an end date
	defaultTime?: { hours: number; minutes: number };
	validate?: (value: Date | undefined) => true | string;
};

/* A required date and time: a calendar and the time under it, in a popover */
const DateTimeField = <TFieldValues extends FieldValues>({
	name,
	label,
	placeholder,
	control,
	minDate,
	defaultTime,
	validate,
}: TProps<TFieldValues>) => (
	<div className="flex flex-col gap-1">
		<Label>
			{label} <span className="text-red-500">*</span>
		</Label>
		<Controller
			name={name}
			control={control}
			rules={{ required: 'Date requise', validate: validate as (value: PathValue<TFieldValues, Path<TFieldValues>>) => true | string }}
			render={({ field: { value, onChange }, fieldState: { error } }) => (
				<>
					<Popover>
						<PopoverTrigger asChild>
							<Button
								type="button"
								variant="outline"
								className={cn(
									'justify-start text-left font-normal text-xs px-3',
									!value && 'text-muted-foreground',
									error && 'border-red-500'
								)}
							>
								<CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
								{value ? format(value, 'dd/MM/yyyy HH:mm', { locale: fr }) : placeholder}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0">
							<Calendar
								mode="single"
								selected={value}
								onSelect={day => {
									if (!day) return onChange(undefined);
									const next = new Date(day);
									// a new day keeps the time already chosen
									if (value) next.setHours(value.getHours(), value.getMinutes());
									else if (defaultTime) next.setHours(defaultTime.hours, defaultTime.minutes);
									onChange(next);
								}}
								autoFocus
								disabled={day => {
									if (!minDate) return false;
									const first = new Date(minDate);
									first.setHours(0, 0, 0, 0);
									return day < first;
								}}
							/>
							<div className="border-t p-3">
								<input
									type="time"
									className="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
									value={value ? format(value, 'HH:mm') : ''}
									onChange={event => {
										const [hours, minutes] = event.target.value.split(':').map(Number);
										const next = value ? new Date(value) : new Date();
										next.setHours(hours, minutes, 0, 0);
										onChange(next);
									}}
								/>
							</div>
						</PopoverContent>
					</Popover>
					{error && <span className="text-xs text-red-500">{error.message}</span>}
				</>
			)}
		/>
	</div>
);

export default DateTimeField;
